import { accentTextStyles } from "@/components/common/accent-text";

type Range = {
  start: number;
  end: number;
};

const parseRange = (rangeStr: string): Range | null => {
  const match = rangeStr.match(/\((\d+)-(\d+)\)/);
  if (!match) return null;

  const start = parseInt(match[1] ?? "0", 10);
  const end = parseInt(match[2] ?? "0", 10);
  return { start, end };
};

/**
 * Interpret colors in a string and wrap them in a span with a class
 * Supports range coloring with syntax: color(start-end):word
 * Example: orange(0-5):hello -> first 5 characters of "hello" will be orange
 * @param title - The string to interpret
 * @returns The interpreted string
 */
export const interpretColors = (title: string): string => {
  const orange = title.match(
    /orange(?:\(\d+-\d+\))?:([^\s]*?)(?=\s|$|orange:|red:|green:|underline:)/g
  );
  const red = title.match(
    /red(?:\(\d+-\d+\))?:([^\s]*?)(?=\s|$|orange:|red:|green:|underline:)/g
  );
  const green = title.match(
    /green(?:\(\d+-\d+\))?:([^\s]*?)(?=\s|$|orange:|red:|green:|underline:)/g
  );
  const underline = title.match(
    /underline(?:\(\d+-\d+\))?:([^\s]*?)(?=\s|$|orange:|red:|green:|underline:)/g
  );

  if (orange) {
    orange.forEach((match) => {
      const range = parseRange(match);
      const word = match.replace(/orange(?:\(\d+-\d+\))?:/, "");

      if (range) {
        const { start, end } = range;
        const before = word.slice(0, start);
        const colored = word.slice(start, end);
        const after = word.slice(end);
        title = title.replace(
          match,
          `${before}<span class='text-brand-yellow'>${colored}</span>${after}`
        );
      } else {
        title = title.replace(
          match,
          `<span class='text-brand-yellow'>${word}</span>`
        );
      }
    });
  }

  if (underline) {
    underline.forEach((match) => {
      const range = parseRange(match);
      const word = match.replace(/underline(?:\(\d+-\d+\))?:/, "");

      if (range) {
        const { start, end } = range;
        const before = word.slice(0, start);
        const colored = word.slice(start, end);
        const after = word.slice(end);
        title = title.replace(
          match,
          `${before}<span class='${accentTextStyles}'>${colored}</span>${after}`
        );
      } else {
        title = title.replace(
          match,
          `<span class='${accentTextStyles}'>${word}</span>`
        );
      }
    });
  }

  if (red) {
    red.forEach((match) => {
      const range = parseRange(match);
      const word = match.replace(/red(?:\(\d+-\d+\))?:/, "");

      if (range) {
        const { start, end } = range;
        const before = word.slice(0, start);
        const colored = word.slice(start, end);
        const after = word.slice(end);
        title = title.replace(
          match,
          `${before}<span class='text-brand-red'>${colored}</span>${after}`
        );
      } else {
        title = title.replace(
          match,
          `<span class='text-brand-red'>${word}</span>`
        );
      }
    });
  }

  if (green) {
    green.forEach((match) => {
      const range = parseRange(match);
      const word = match.replace(/green(?:\(\d+-\d+\))?:/, "");

      if (range) {
        const { start, end } = range;
        const before = word.slice(0, start);
        const colored = word.slice(start, end);
        const after = word.slice(end);
        title = title.replace(
          match,
          `${before}<span class='text-brand-green1-300'>${colored}</span>${after}`
        );
      } else {
        title = title.replace(
          match,
          `<span class='text-brand-green1-300'>${word}</span>`
        );
      }
    });
  }

  return title;
};
